import React, { Suspense, lazy, useCallback, useEffect, useRef } from 'react';
import { Bookmark, GitCompare, Sparkles } from 'lucide-react';
import { useUIStore } from '../../stores/uiStore';
import { AI_VIEW, useSideDock, type DockTool } from '../../stores/sideDockStore';
import { useCompareStore } from '../../stores/compareStore';
import { useFollowStore } from '../../stores/followStore';
import { ComparePane, FollowPane } from './DockPanes';
import AiPanelResizer from '../workspace/AiPanelResizer';
import { clampAiWidth } from '../workspace/aiPanelWidth';

const AiPanel = lazy(() => import('../workspace/AiPanel'));

/** the rail's width — the panel's resize handle measures from the window's edge past it */
const RAIL_W = 44;

/**
 * The right side of every page: a rail of icons (the AI assistant, then the open page's
 * tools — sideDockStore) and the chosen one's panel beside it. One column instead of the AI
 * panel plus each page's own right-hand panel.
 */
const SideDock: React.FC<{ wide: boolean }> = ({ wide }) => {
  const collapsed = useUIStore((s) => s.aiPanelCollapsed);
  const aiPanelWidth = useUIStore((s) => s.aiPanelWidth);
  const aiPageWidth = useUIStore((s) => s.aiPageWidth);
  const setAiWidth = useUIStore((s) => s.setAiWidth);
  const { tools, view, toggle, show, setPanelEl } = useSideDock();
  const panelRef = useRef<HTMLDivElement>(null);
  const compareCount = useCompareStore((s) => s.selectedIds.length);
  const followCount = useFollowStore((s) => s.items.length);
  // on every page, under the AI: the tenders picked to compare, and what is followed
  const builtins: DockTool[] = [
    { id: 'compare', label: 'Харьцуулах', icon: GitCompare, badge: compareCount },
    { id: 'follow', label: 'Дагаж буй', icon: Bookmark, badge: followCount },
  ];
  // the first tender picked to compare opens the list (the old floating bar's job)
  const prevCompare = useRef(compareCount);
  useEffect(() => {
    if (prevCompare.current === 0 && compareCount > 0) show('compare');
    prevCompare.current = compareCount;
  }, [compareCount, show]);

  const width = wide ? aiPageWidth : aiPanelWidth;
  const setWidth = useCallback((w: number | null) => setAiWidth(wide, w == null ? null : clampAiWidth(w - RAIL_W)), [wide, setAiWidth]);
  const getWidth = useCallback(() => (panelRef.current?.getBoundingClientRect().width ?? 320) + RAIL_W, []);
  const big = clampAiWidth(window.innerWidth * 0.6);
  const maximized = width != null && width >= big - 40;
  const toggleMaximize = useCallback(() => setAiWidth(wide, maximized ? null : big), [wide, maximized, big, setAiWidth]);
  const close = useCallback(() => useUIStore.setState({ aiPanelCollapsed: true }), []);

  const open = !collapsed;
  const shown = open && (view === AI_VIEW || builtins.some((t) => t.id === view) || tools.some((t) => t.id === view)) ? view : null;

  return (
    <div className={`app-dock${shown ? ' is-open' : ''}`}>
      {shown && (
        <div
          ref={panelRef}
          className={`app-ai-panel app-dock-panel${wide && shown === AI_VIEW ? ' app-ai-panel--wide' : ''}`}
          style={width ? { flexBasis: clampAiWidth(width) } : undefined}
        >
          <AiPanelResizer getWidth={getWidth} onResize={setWidth} onReset={() => setAiWidth(wide, null)} />
          {shown === AI_VIEW ? (
            <Suspense fallback={null}>
              <AiPanel collapsed={false} onToggle={close} maximized={maximized} onToggleMaximize={toggleMaximize} />
            </Suspense>
          ) : shown === 'compare' ? <ComparePane onClose={close} />
            : shown === 'follow' ? <FollowPane onClose={close} />
            : (
            // the page renders the tool's panel in here (useDockTools → DockPanel)
            <div className="app-dock-host" ref={setPanelEl} />
          )}
        </div>
      )}
      <nav className="app-dock-rail" aria-label="Хажуугийн самбар">
        <button type="button" className={`app-dock-btn app-dock-ai${shown === AI_VIEW ? ' on' : ''}`}
          onClick={() => toggle(AI_VIEW)} title="AI туслах" aria-label="AI туслах" aria-pressed={shown === AI_VIEW}>
          <Sparkles size={17} />
        </button>
        {builtins.map((t) => (
          <button key={t.id} type="button" className={`app-dock-btn${shown === t.id ? ' on' : ''}`}
            onClick={() => toggle(t.id)} title={t.label} aria-label={t.label} aria-pressed={shown === t.id}>
            <t.icon size={17} />
            {!!t.badge && <span className="app-dock-badge">{t.badge}</span>}
          </button>
        ))}
        {tools.length > 0 && <span className="app-dock-sep" aria-hidden />}
        {tools.map((t) => (
          <button key={t.id} type="button" className={`app-dock-btn${shown === t.id ? ' on' : ''}`}
            onClick={() => toggle(t.id)} title={t.label} aria-label={t.label} aria-pressed={shown === t.id}>
            <t.icon size={17} />
            {!!t.badge && <span className="app-dock-badge">{t.badge}</span>}
          </button>
        ))}
      </nav>
    </div>
  );
};

export default SideDock;
